import {
  Button,
  DialogActions,
  DialogContent,
  DialogForm,
  Divider,
  FormRadioButtonGroup,
  RadioButton,
} from 'omakase';
import { FormTextField, Grid } from 'ui';
import { Formik, FormikErrors } from 'formik';
import { PLAYLIST_DEFAULTS } from 'selectors/patronSelectors';
import { consumer, core, payloadToJSON } from '@tock/coretypes';
import { useActions } from '@tock/shared/utils/connect';
import _ from 'lodash';

interface Props {
  wrapper?(content: React.ReactNode): React.ReactNode;
  business?: core.IBusiness;
  onSaveDone(): void;
  playlist?: consumer.Playlist;
}

interface PlaylistFormFields {
  id?: number;
  name: string;
  description?: string;
  listType: ListPermissions;
}

export type ListPermissions = 'PUBLIC' | 'SHARED' | 'PRIVATE';

const validate = (values: PlaylistFormFields) => {
  const formikErrors: FormikErrors<PlaylistFormFields> = {};

  if (_.isEmpty(values.name)) {
    formikErrors.name = 'Name is required';
  }

  if (values.name.length > 50) {
    formikErrors.name = 'Name must be less than 50 characters';
  }

  if ((values.description?.length || 0) > 255) {
    formikErrors.description = 'Description must be less than 255 characters';
  }

  return formikErrors;
};

const BusinessPlaylistsDialogListPage: React.FunctionComponent<Props> = ({
  business,
  wrapper = (content) => <>{content}</>,
  onSaveDone,
  playlist,
}) => {
  const { actions } = useActions();

  const handleSubmit = (values: PlaylistFormFields) => {
    return actions.patron
      .upsertPlaylist({
        id: values.id,
        name: values.name,
        description: values.description,
        isPublic: values.listType === 'PUBLIC',
      })
      .then((response) => {
        const playlist = payloadToJSON<consumer.Playlist>(response.payload);

        if (business) {
          return actions.patron.upsertPlaylistItem(playlist.id, { businessId: business.id });
        }
        return;
      })
      .then(onSaveDone);
  };

  const isDefaultList = (playlist?.name && PLAYLIST_DEFAULTS.includes(playlist.name)) || false;

  const getContent = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormTextField id="name" name="name" fullWidth label="Name" disabled={isDefaultList} />
        </Grid>
        <Grid item xs={12}>
          <FormTextField
            id="description"
            name="description"
            fullWidth
            label="Description"
            multiline
          />
        </Grid>
        <Divider mt={3} mb={3} />
        <Grid item xs={12}>
          <FormRadioButtonGroup disclosureMode="static" name="listType" label="List type">
            <RadioButton
              size="large"
              value="PRIVATE"
              label="Private"
              helperText="Only you can view and edit."
            />
            <RadioButton
              size="large"
              value="PUBLIC"
              label="Public"
              helperText="This list will appear publicly, and is accessible to anyone. Your content must comply with Tock's policies."
            />
          </FormRadioButtonGroup>
        </Grid>
      </Grid>
    );
  };

  return (
    <Formik<PlaylistFormFields>
      validate={validate}
      initialValues={{
        id: playlist?.id,
        name: playlist?.name || '',
        listType: playlist?.isPublic ? 'PUBLIC' : 'PRIVATE',
        description: playlist?.description || undefined,
      }}
      onSubmit={handleSubmit}
    >
      <DialogForm>
        <DialogContent>{wrapper(getContent())}</DialogContent>
        <DialogActions>
          <Button variant="primary" size="large" type="submit" fullWidth>
            Save
          </Button>
        </DialogActions>
      </DialogForm>
    </Formik>
  );
};

export default BusinessPlaylistsDialogListPage;
